import type { StepConfiguration } from '../HangingProtocol/stepConversions';
import { isViewportInLayoutBounds } from '../HangingProtocol/steppedHPTypes';
import {
  decodeCompositeStackSmid,
  decodeOverlayStackSmidToStackLayers,
  isStackSmidMultiLayer,
} from './stackUtils';
import { viewportIdToConfig } from './viewerUtils';

/**
 * VX-5244: how many steps ahead of the active one each stack is first shown, keyed by stack smid (1 = the next step).
 *
 * Distance counts forward from the active step and wraps, so steps the reader has already passed rank after every
 * upcoming one: radiologists read steps in order, and a passed step was hanged while it was shown. A stack shown by
 * several steps takes its nearest one. The active step itself is not counted -- what it shows comes from the live
 * viewport configurations, which also carry the reader's drops, and callers check those first -- and any stack no step
 * shows is absent.
 *
 * A multi-layer or composite viewport is ranked through the stacks behind it, which are the ones that load pixels. Its
 * own provider owns none, and for a step not yet shown it has not even been created.
 *
 * Only viewports inside a step's layout on an open window count, since nothing else in the step is ever rendered. Reads
 * the server-resolved `stack_smid`, so an upcoming step's own drops are not seen here; a dropped stack is hanged as
 * soon as its step is shown.
 */
export function getUpcomingStepDistances({
  steps,
  activeStepIndex,
  openWindowIds,
}: {
  steps: Nullable<ReadonlyArray<StepConfiguration>>;
  activeStepIndex: number;
  openWindowIds: ReadonlyArray<string>;
}): Map<string, number> {
  const distances = new Map<string, number>();
  if (steps == null || steps.length < 2) return distances;
  // Past the end means no step is applied (the HP form navigating its own steps), so there is nothing to count from.
  if (activeStepIndex < 0 || activeStepIndex >= steps.length) return distances;

  for (let distance = 1; distance < steps.length; distance++) {
    const step = steps[(activeStepIndex + distance) % steps.length];
    if (step == null) continue;

    for (const [viewportId, viewport] of Object.entries(step.viewports)) {
      const stackSmid = viewport.stack_smid;
      if (stackSmid == null) continue;

      const { windowId } = viewportIdToConfig(viewportId);
      if (
        !openWindowIds.includes(windowId) ||
        !isViewportInLayoutBounds(viewportId, step.layouts)
      ) {
        continue;
      }

      for (const pixelStackSmid of getPixelStackSmids(stackSmid)) {
        if (!distances.has(pixelStackSmid)) distances.set(pixelStackSmid, distance);
      }
    }
  }

  return distances;
}

/** The smids of the single-layer stacks a viewport's stack smid loads pixels through. */
function getPixelStackSmids(stackSmid: string): ReadonlyArray<string> {
  const composite = decodeCompositeStackSmid(stackSmid);
  if (composite != null) return [composite.topSmid, ...composite.matchedSmids];
  if (isStackSmidMultiLayer(stackSmid)) {
    return decodeOverlayStackSmidToStackLayers(stackSmid).map((layer) => layer.stackSmid);
  }
  return [stackSmid];
}
